import { z } from 'zod';

// ============================================
// REGISTRATION
// ============================================
export const passwordSchema = z
  .string()
  .min(12, 'Passwort muss mindestens 12 Zeichen haben')
  .refine((val) => /[A-Z]/.test(val), 'Mindestens ein Grossbuchstabe erforderlich')
  .refine((val) => /[0-9]/.test(val), 'Mindestens eine Ziffer erforderlich')
  .refine((val) => /[^A-Za-z0-9]/.test(val), 'Mindestens ein Sonderzeichen erforderlich');

export const registrationSchema = z.object({
  invitationCode: z.string().regex(/^[A-HJ-KM-NP-Z2-9]{4}-[A-HJ-KM-NP-Z2-9]{4}-[A-HJ-KM-NP-Z2-9]{4}$/, 'Ungueltiges Einladungscode-Format'),
  companyName: z.string().min(2).max(200).trim(),
  rechtsform: z.string().min(1).max(100).trim(),
  sitzland: z.string().min(2).max(50).trim(),
  handelsregister: z.string().min(2).max(200).trim(),
  ust_id: z.string().max(20).optional().or(z.literal('')),
  vorname: z.string().min(1).max(100).trim(),
  nachname: z.string().min(1).max(100).trim(),
  position: z.string().min(1).max(100).trim(),
  email: z.string().email('Ungueltige E-Mail-Adresse'),
  telefon: z.string().min(6).max(30).trim(),
  password: passwordSchema,
  acceptedAGB: z.literal(true, { errorMap: () => ({ message: 'AGB muessen akzeptiert werden' }) }),
  acceptedDatenschutz: z.literal(true, { errorMap: () => ({ message: 'Datenschutzerklaerung muss akzeptiert werden' }) }),
  confirmedB2B: z.literal(true, { errorMap: () => ({ message: 'Sie muessen bestaetigen, dass sie fuer ein Unternehmen handeln' }) }),
});

export type RegistrationInput = z.infer<typeof registrationSchema>;

// ============================================
// BID
// ============================================
export const bidSchema = z.object({
  auction_id: z.string().uuid('Ungueltige Auktions-ID'),
  betrag: z.coerce.number()
    .positive('Betrag muss groesser als 0 sein')
    .max(999999999.99, 'Betrag zu gross'),
});

export type BidInput = z.infer<typeof bidSchema>;

// ============================================
// INQUIRY
// ============================================
export const inquirySchema = z.object({
  auction_id: z.string().uuid('Ungueltige Auktions-ID'),
  frage: z.string().min(20, 'Frage muss mindestens 20 Zeichen haben').max(2000, 'Frage darf maximal 2000 Zeichen haben'),
});

export type InquiryInput = z.infer<typeof inquirySchema>;

// ============================================
// AUCTION (Admin)
// ============================================
export const strategicSectionSchema = z.object({
  section_id: z.string().min(1).max(100),
  titel: z.string().min(1).max(300),
  body: z.string(),
});

export const auctionCreateSchema = z.object({
  aktenzeichen: z.string().regex(/^AUKT-\d{4}-\d{4}$/, 'Aktenzeichen-Format: AUKT-JAHR-NNNN'),
  titel: z.string().min(5).max(300),
  patent_nummer: z.string().min(5).max(100),
  anmeldetag: z.string().date(),
  erteilungstag: z.string().date(),
  ablaufdatum: z.string().date(),
  schutzlaender: z.array(z.string().min(2).max(2)).nonempty(),
  ipc_klasse: z.string().min(3).max(50),
  aktueller_inhaber: z.string().min(2).max(200),
  jahresgebuehren_bis: z.string().regex(/^\d{4}$/),
  technische_zusammenfassung: z.string().min(50),
  strategische_einordnung: z.array(strategicSectionSchema),
  mindestgebot: z.coerce.number().positive(),
  mindest_schrittweite: z.coerce.number().positive(),
  reservepreis: z.coerce.number().positive().optional(),
  start_at: z.string().datetime({ offset: true }),
  end_at: z.string().datetime({ offset: true }),
  compliance_frist_tage: z.coerce.number().int().min(1).max(90).default(10),
});

export type AuctionCreateInput = z.infer<typeof auctionCreateSchema>;

// ============================================
// INVITATION CODE
// ============================================
export const invitationCodeGenerateSchema = z.object({
  count: z.coerce.number().int().min(1).max(100).default(1),
  valid_until: z.string().datetime({ offset: true }).optional(),
  auction_ids: z.array(z.string().uuid()).default([]),
});

// ============================================
// ANALYTICS
// ============================================
export const trackViewSchema = z.object({
  action: z.enum(['page_view', 'section_focus_batch']),
  auction_id: z.string().uuid(),
  session_id: z.string().uuid().optional(),
  referrer: z.string().optional(),
  page_view_id: z.string().uuid().optional(),
  sections: z.array(z.object({
    section_id: z.string().min(1).max(100),
    focus_seconds: z.coerce.number().int().min(0).max(3600),
  })).optional(),
});

// ============================================
// LOGIN
// ============================================
export const loginSchema = z.object({
  email: z.string().email('Ungueltige E-Mail-Adresse'),
  password: z.string().min(1, 'Passwort ist erforderlich'),
});

export type LoginInput = z.infer<typeof loginSchema>;

// ============================================
// PASSWORD RESET
// ============================================
export const passwordResetSchema = z.object({
  email: z.string().email('Ungueltige E-Mail-Adresse'),
});

// ============================================
// COMPLIANCE
// ============================================
export const complianceUpdateSchema = z.object({
  kyc_dokumente_eingegangen: z.boolean().optional(),
  sanktionslistenpruefung_bestanden: z.boolean().optional(),
  zahlungseingang_bestaetigt: z.boolean().optional(),
  notizen_admin: z.string().max(2000).optional(),
});
